import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Plus, Printer, RefreshCw, Save, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/Modal";
import SearchSelect from "../../components/SearchSelect";
import SidebarLayout from "../../components/SidebarLayout";
import BillingPrintDocument, { instructionText } from "../../components/BillingPrintDocument";
import { BillingVisit, Medicine, PrescriptionItem } from "./billingTypes";
import { BACKEND_URL } from "../../config";
import { useAuth } from "../../modules/auth/authContext";
import { formatPatientName } from "../../utils/patientName";
import { formatVisitLabel } from "../../utils/visitNumber";
import { isWeeklyFrequency, doseAmount, formatDoseSchedule as frequencySchedule, medicineDoseConfig } from "../../utils/medicineDose";

interface BillLine {
  key: string;
  source: "doctor" | "append";
  billItemId?: number;
  type: "medicine" | "service" | "lab";
  medicine_id?: number;
  description: string;
  batchNo?: string;
  expiryDate?: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  note?: string;
}

interface AppendDraft {
  type: "medicine" | "service" | "lab";
  item: Medicine | null;
  quantity: number;
}

const formatDate = (value?: string | Date) => {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
};

const medicineLabel = (item: PrescriptionItem) => {
  const medicine = item.medicine;
  const name = medicine?.brand || medicine?.name || item.medicine_name || "Medicine";
  if (medicine?.type === "Tablet") return `Tab. ${name}`;
  if (medicine?.type === "Capsule") return `Cap. ${name}`;
  return name;
};

const catalogLabel = (item: Medicine) => {
  const name = item.brand || item.name || "Item";
  if (item.type === "Tablet") return `Tab. ${name}`;
  if (item.type === "Capsule") return `Cap. ${name}`;
  return name;
};

const frequencyDosePerDay = (frequency?: string) => {
  if (!frequency) return 0;
  if (isWeeklyFrequency(frequency)) return 1 / 7;
  if (["OD-M", "OD-A", "OD-N"].includes(frequency)) return 1;
  const parts = frequency.split("|").map((part) => part.trim());
  const numericPart = parts[parts.length - 1] || "";
  const parsed = Number(numericPart);
  if (!Number.isNaN(parsed)) return parsed;

  const lowerFrequency = frequency.toLowerCase();
  if (lowerFrequency.includes("four")) return 4;
  if (lowerFrequency.includes("three")) return 3;
  if (lowerFrequency.includes("twice")) return 2;
  if (lowerFrequency.includes("once")) return 1;
  return 0;
};

const durationDays = (duration?: string) => {
  if (!duration) return 0;
  const match = String(duration).match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : 0;
};

const calculateMedicineQuantity = (item: Pick<PrescriptionItem, "frequency" | "duration" | "quantity" | "count_per_time" | "medicine">) => {
  if (!medicineDoseConfig(item.medicine).autoQuantity) return Number(item.quantity || 1);
  const dosesPerDay = frequencyDosePerDay(item.frequency);
  const days = durationDays(item.duration);
  const countPerTime = doseAmount(item.count_per_time);
  const calculatedQuantity = dosesPerDay > 0 && days > 0 ? Math.ceil(dosesPerDay * days * countPerTime) : 0;
  return calculatedQuantity || Number(item.quantity || 1);
};

const AddOrEditBill: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [visits, setVisits] = useState<BillingVisit[]>([]);
  const [selectedVisit, setSelectedVisit] = useState<BillingVisit | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState({ open: false, title: "", message: "" });
  const [paymentConfirmOpen, setPaymentConfirmOpen] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [appendDraft, setAppendDraft] = useState<AppendDraft>({ type: "medicine", item: null, quantity: 1 });
  const [appendSearchClear, setAppendSearchClear] = useState(0);
  const [appendedLines, setAppendedLines] = useState<BillLine[]>([]);

  const loadBillingVisits = async () => {
    setLoading(true);
    try {
      const [billingRes, paymentRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/visits?status=BILLING_PENDING`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/visits?status=PAYMENT_PENDING`, { credentials: "include" }),
      ]);
      if (!billingRes.ok) throw new Error(await billingRes.text());
      if (!paymentRes.ok) throw new Error(await paymentRes.text());
      const data = [...await billingRes.json(), ...await paymentRes.json()];
      setVisits(data);
      if (selectedVisit) {
        setSelectedVisit(data.find((visit: BillingVisit) => visit.id === selectedVisit.id) || null);
      }
    } catch (err: any) {
      setModal({ open: true, title: "Unable to load visits", message: err.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBillingVisits();
  }, []);

  useEffect(() => {
    setAppendedLines([]);
    setAppendDraft({ type: "medicine", item: null, quantity: 1 });
    setAppendSearchClear((value) => value + 1);
  }, [selectedVisit?.id]);

  const contact = selectedVisit?.patient?.Contact;
  const prescriptionItems = selectedVisit?.prescription?.items || [];

  const billLines = useMemo<BillLine[]>(() => {
    if (!selectedVisit) return [];

    const medicineLines = prescriptionItems.map((item) => {
      const quantity = calculateMedicineQuantity(item);
      const unitPrice = Number(item.medicine?.mrpPrice || 0);
      return {
        key: `medicine-${item.id}`,
        source: "doctor",
        type: "medicine",
        medicine_id: item.medicine_id || item.medicine?.id,
        description: medicineLabel(item),
        batchNo: item.medicine?.batchNo,
        expiryDate: item.medicine?.expiryDate,
        quantity,
        unitPrice,
        totalAmount: quantity * unitPrice,
        note: [frequencySchedule(item), item.duration, instructionText(item)].filter(Boolean).join(" | "),
      };
    });
    const serviceLines = (selectedVisit.service_orders || []).map((service) => {
      const quantity = Math.max(1, Math.ceil(Number(service.quantity || 1)));
      const unitPrice = Number(service.price || 0);
      return {
        key: `service-order-${service.id}`,
        source: "doctor" as const,
        type: "service" as const,
        description: service.service_name,
        quantity,
        unitPrice,
        totalAmount: quantity * unitPrice,
        note: "Doctor requested service",
      };
    });
    const labLines = (selectedVisit.lab_orders || []).map((lab) => ({
      key: `lab-order-${lab.id}`,
      source: "doctor" as const,
      type: "lab" as const,
      description: lab.test_name,
      quantity: 1,
      unitPrice: 0,
      totalAmount: 0,
      note: "Doctor requested lab service",
    }));

    return [...medicineLines, ...serviceLines, ...labLines];
  }, [selectedVisit?.id, prescriptionItems]);

  const persistedBillLines = useMemo<BillLine[]>(() => {
    if (!selectedVisit?.bill) return [];

    const medicineLines = (selectedVisit.bill.items || []).map((item) => {
      const quantity = Number(item.quantity || 1);
      const unitPrice = Number(item.medicine?.mrpPrice || 0);
      return {
        key: `bill-medicine-${item.id}`,
        source: "doctor" as const,
        type: "medicine" as const,
        medicine_id: item.medicine?.id,
        description: catalogLabel(item.medicine || { id: 0, name: "Medicine" }),
        batchNo: item.medicine?.batchNo,
        expiryDate: item.medicine?.expiryDate,
        quantity,
        unitPrice,
        totalAmount: Number(item.totalAmount || quantity * unitPrice),
        note: "Saved bill medicine",
      };
    });

    const extraLines = (selectedVisit.bill.bill_items || []).map((item) => ({
      key: `bill-extra-${item.id}`,
      source: item.source === "append" ? "append" as const : "doctor" as const,
      billItemId: item.id,
      type: item.item_type === "lab" ? "lab" as const : item.item_type === "service" ? "service" as const : "medicine" as const,
      medicine_id: item.item_id,
      description: item.description,
      quantity: Number(item.quantity || 1),
      unitPrice: Number(item.unit_price || 0),
      totalAmount: Number(item.total_price || 0),
      note: "Saved billing item",
    }));

    return [...medicineLines, ...extraLines];
  }, [selectedVisit?.bill?.id]);

  const baseBillLines = selectedVisit?.bill ? persistedBillLines : billLines;
  const allBillLines = useMemo(() => [...baseBillLines, ...appendedLines], [baseBillLines, appendedLines]);
  const savedBillingAddedLines = useMemo(() => persistedBillLines.filter((line) => line.source === "append" && line.billItemId), [persistedBillLines]);
  const billingAddedLines = useMemo(() => [...savedBillingAddedLines, ...appendedLines], [savedBillingAddedLines, appendedLines]);
  const billableLines = allBillLines.filter((line) => (line.medicine_id || line.type !== "medicine") && line.totalAmount > 0);
  const totalAmount = allBillLines.reduce((sum, line) => sum + line.totalAmount, 0);

  const addAppendLine = () => {
    if (!appendDraft.item?.id) return;
    const quantity = appendDraft.type === "lab" ? 1 : Math.max(1, Math.ceil(Number(appendDraft.quantity || 1)));
    const unitPrice = Number(appendDraft.item.mrpPrice || 0);
    const typeLabel = appendDraft.type.charAt(0).toUpperCase() + appendDraft.type.slice(1);

    setAppendedLines((prev) => [
      ...prev,
      {
        key: `append-${appendDraft.type}-${appendDraft.item?.id}-${Date.now()}`,
        source: "append",
        type: appendDraft.type,
        medicine_id: appendDraft.item?.id,
        description: catalogLabel(appendDraft.item),
        batchNo: appendDraft.item.batchNo,
        expiryDate: appendDraft.item.expiryDate,
        quantity,
        unitPrice,
        totalAmount: quantity * unitPrice,
        note: `${typeLabel} added by billing`,
      },
    ]);
    setAppendDraft((prev) => ({ ...prev, item: null, quantity: 1 }));
    setAppendSearchClear((value) => value + 1);
  };

  const createBill = async () => {
    if (!selectedVisit) return;
    const newLines = selectedVisit.bill ? appendedLines.filter((line) => (line.medicine_id || line.type !== "medicine") && line.totalAmount > 0) : billableLines;
    if (!newLines.length) {
      if (selectedVisit.bill && selectedVisit.status === "BILLING_PENDING") {
        setSaving(true);
        try {
          const res = await fetch(`${BACKEND_URL}/api/visits/${selectedVisit.id}/send-to-payment`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
          if (!res.ok) throw new Error(await res.text());
          const updatedVisit = await res.json();
          setSelectedVisit((prev) => prev ? { ...prev, ...updatedVisit } : updatedVisit);
          setModal({ open: true, title: "Bill ready", message: "The bill is ready for payment confirmation." });
          await loadBillingVisits();
        } catch (err: any) {
          setModal({ open: true, title: "Unable to update bill", message: err.message || "Please try again." });
        } finally {
          setSaving(false);
        }
        return;
      }

      setModal({
        open: true,
        title: selectedVisit.bill ? "No new billing items" : "No billable items",
        message: selectedVisit.bill ? "Add medicine, service, or lab items before saving." : "This visit does not have billable items ready for billing.",
      });
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(selectedVisit.bill ? `${BACKEND_URL}/api/billing/${selectedVisit.bill.id}/items` : `${BACKEND_URL}/api/billing/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(selectedVisit.bill ? {
          items: newLines.map((line) => ({
            medicine_id: line.medicine_id,
            item_type: line.type,
            source: line.source,
            description: line.description,
            unitPrice: line.unitPrice,
            quantity: line.quantity,
            totalAmount: line.totalAmount,
          })),
        } : {
          visit_id: selectedVisit.id,
          prescription_id: selectedVisit.prescription?.id,
          patient_id: selectedVisit.patient_id,
          doctor_id: selectedVisit.assigned_doctor_id,
          billedBy_id: typeof user?.id === "string" ? parseInt(user.id, 10) : user?.id || 0,
          date: new Date().toISOString().split("T")[0],
          items: newLines.map((line) => ({
            medicine_id: line.medicine_id,
            item_type: line.type,
            source: line.source,
            description: line.description,
            unitPrice: line.unitPrice,
            quantity: line.quantity,
            totalAmount: line.totalAmount,
          })),
        }),
      });

      if (!res.ok) throw new Error(await res.text());
      setModal({ open: true, title: selectedVisit.bill ? "Bill updated" : "Bill created", message: "The bill is ready for print." });
      setAppendedLines([]);
      await loadBillingVisits();
    } catch (err: any) {
      setModal({ open: true, title: "Unable to create bill", message: err.message || "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const confirmPayment = async () => {
    if (!selectedVisit) return;

    setPaymentSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/visits/${selectedVisit.id}/close`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(await res.text());

      setPaymentConfirmOpen(false);
      setSelectedVisit(null);
      setAppendedLines([]);
      setModal({
        open: true,
        title: "Payment Confirmed",
        message: "Money collected. The visit has been completed and removed from billing.",
      });
      await loadBillingVisits();
    } catch (err: any) {
      setModal({ open: true, title: "Unable to confirm payment", message: err.message || "Please try again." });
    } finally {
      setPaymentSaving(false);
    }
  };

  const removeSavedBillingItem = async (line: BillLine) => {
    if (!selectedVisit?.bill?.id || !line.billItemId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/${selectedVisit.bill.id}/items/${line.billItemId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      const updatedBill = await res.json();
      setSelectedVisit((prev) => prev ? { ...prev, bill: updatedBill } : prev);
      setModal({ open: true, title: "Billing item removed", message: "The billing-added item was removed from the bill." });
    } catch (err: any) {
      setModal({ open: true, title: "Unable to remove item", message: err.message || "Please try again." });
    }
  };

  return (
    <SidebarLayout title="Create Bill" subtitle="Select billing-ready visits and print the prescription with medicine bill.">
      <Modal open={modal.open} title={modal.title} message={modal.message} onClose={() => setModal((prev) => ({ ...prev, open: false }))} />
      <Modal
        open={paymentConfirmOpen}
        title="Confirm Payment"
        message="Money collected?"
        onClose={() => setPaymentConfirmOpen(false)}
      >
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setPaymentConfirmOpen(false)}
            className="rounded border border-border-light px-4 py-2 font-semibold text-subtle-light hover:bg-background-light"
          >
            No
          </button>
          <button
            type="button"
            onClick={confirmPayment}
            disabled={paymentSaving}
            className="rounded bg-success-green px-4 py-2 font-semibold text-white disabled:opacity-50"
          >
            {paymentSaving ? "Confirming..." : "Yes, Complete Visit"}
          </button>
        </div>
      </Modal>

      <div className="grid min-w-0 grid-cols-1 gap-5 xl:grid-cols-[380px_minmax(0,1fr)]">
        <aside className="rounded border border-border-light bg-surface-light p-5 print:hidden">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-xl font-bold text-text-light">Billing Visits</h2>
            <button
              type="button"
              onClick={loadBillingVisits}
              className="inline-flex items-center gap-2 rounded border border-border-light px-3 py-2 text-sm font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="rounded bg-background-light p-4 text-sm text-subtext-light">Loading visits...</div>
          ) : visits.length ? (
            <div className="space-y-3">
              {visits.map((visit) => {
                const patient = visit.patient?.Contact;
                const selected = selectedVisit?.id === visit.id;
                return (
                  <button
                    key={visit.id}
                    type="button"
                    onClick={() => setSelectedVisit(visit)}
                    className={`w-full rounded border p-4 text-left transition ${
                      selected ? "border-primary bg-primary/10" : "border-border-light bg-white hover:bg-primary/5"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-bold text-text-light">{formatPatientName(patient, "Unnamed Patient")}</span>
                      <span className={`rounded-full px-2 py-1 text-xs font-bold ${visit.status === "PAYMENT_PENDING" ? "bg-success-green/10 text-success-green" : "bg-warning-amber/15 text-warning-amber"}`}>
                        {visit.status === "PAYMENT_PENDING" ? "Payment Pending" : "Billing"}
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-subtext-light">
                      <div>{formatVisitLabel(visit)}</div>
                      <div>{patient?.phone || "-"}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="rounded border border-dashed border-border-light bg-background-light p-4 text-sm text-subtext-light">
              No visits are waiting for billing.
            </div>
          )}
        </aside>

        <main className="min-w-0 flex flex-col gap-4">
          <div className="flex flex-wrap justify-between gap-3 rounded border border-border-light bg-surface-light p-4 print:hidden">
            <button type="button" onClick={() => navigate("/billing")} className="inline-flex items-center gap-2 rounded border border-border-light px-4 py-2 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary">
              <ArrowLeft size={18} />
              Back
            </button>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => window.print()} disabled={!selectedVisit} className="inline-flex items-center gap-2 rounded border border-border-light px-4 py-2 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary disabled:opacity-50">
                <Printer size={18} />
                Print
              </button>
              <button type="button" onClick={createBill} disabled={!selectedVisit || saving} className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50">
                <Save size={18} />
                {saving ? "Saving..." : selectedVisit?.bill && appendedLines.length ? "Save Added Items" : selectedVisit?.bill ? "Ready for Payment" : "Create Bill"}
              </button>
              {selectedVisit?.status === "PAYMENT_PENDING" && (
                <button
                  type="button"
                  onClick={() => setPaymentConfirmOpen(true)}
                  className="inline-flex items-center gap-2 rounded bg-success-green px-4 py-2 font-semibold text-white hover:bg-success-green/90"
                >
                  Confirm Payment
                </button>
              )}
            </div>
          </div>

          {!selectedVisit ? (
            <section className="rounded border border-dashed border-border-light bg-surface-light p-8 text-center text-subtext-light">
              Select a billing visit to view the prescription and medicine bill.
            </section>
          ) : (
            <>
              <section className="rounded border border-border-light bg-surface-light p-5 print:hidden">
                <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                  <div>
                    <h3 className="text-lg font-bold text-text-light">Append Billing Items</h3>
                    <p className="text-sm text-subtext-light">Doctor-added prescription medicines are locked. Add extra medicines, services, or lab services below.</p>
                  </div>
                  <div className="text-sm font-bold text-text-light">Current total: Rs {totalAmount.toFixed(2)}</div>
                </div>

                <div className="grid grid-cols-1 gap-3 xl:grid-cols-[170px_minmax(0,1fr)_120px_auto] xl:items-end">
                  <label>
                    <div className="mb-1 text-sm font-semibold">Type</div>
                    <select
                      value={appendDraft.type}
                      onChange={(e) => {
                        setAppendDraft({ type: e.target.value as AppendDraft["type"], item: null, quantity: 1 });
                        setAppendSearchClear((value) => value + 1);
                      }}
                      className="w-full rounded border border-border-light bg-white p-3"
                    >
                      <option value="medicine">Medicine</option>
                      <option value="service">Service</option>
                      <option value="lab">Lab Service</option>
                    </select>
                  </label>
                  <SearchSelect<Medicine>
                    label={appendDraft.type === "medicine" ? "Medicine" : appendDraft.type === "service" ? "Service" : "Lab Service"}
                    fetchUrl={`${BACKEND_URL}/api/medicines/search${appendDraft.type === "medicine" ? "" : appendDraft.type === "service" ? "?category=SERVICE" : "?category=LAB"}`}
                    displayKey="name"
                    clearSignal={appendSearchClear}
                    onSelect={(item) => setAppendDraft((prev) => ({ ...prev, item }))}
                  />
                  <label>
                    <div className="mb-1 text-sm font-semibold">Qty</div>
                    <input
                      type="number"
                      min={1}
                      disabled={appendDraft.type === "lab"}
                      value={appendDraft.quantity}
                      onChange={(e) => setAppendDraft((prev) => ({ ...prev, quantity: Math.max(1, Math.ceil(Number(e.target.value || 1))) }))}
                      className="w-full rounded border border-border-light bg-white p-3 disabled:cursor-not-allowed disabled:bg-background-light disabled:text-subtext-light"
                    />
                  </label>
                  <button type="button" onClick={addAppendLine} disabled={!appendDraft.item} className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-3 font-semibold text-white disabled:opacity-50">
                    <Plus size={18} />
                    Add
                  </button>
                </div>

                {billingAddedLines.length > 0 && (
                  <div className="mt-4 overflow-x-auto rounded border border-border-light">
                    <div className="min-w-[520px]">
                      <div className="grid grid-cols-[110px_minmax(0,1fr)_70px_110px_44px] gap-3 bg-background-light px-3 py-2 text-xs font-bold uppercase tracking-wide text-subtext-light">
                        <span>Type</span>
                        <span>Item</span>
                        <span>Qty</span>
                        <span>Total</span>
                        <span></span>
                      </div>
                      {billingAddedLines.map((line) => (
                        <div key={line.key} className="grid grid-cols-[110px_minmax(0,1fr)_70px_110px_44px] items-center gap-3 border-t border-border-light px-3 py-2 text-sm">
                          <span className="capitalize text-subtext-light">{line.type}</span>
                          <span className="min-w-0">
                            <span className="block truncate font-semibold text-text-light">{line.description}</span>
                            <span className="text-xs text-subtext-light">{line.billItemId ? "Saved billing item" : "Unsaved billing item"}</span>
                          </span>
                          <span>{line.quantity}</span>
                          <span>Rs {line.totalAmount.toFixed(2)}</span>
                          <button
                            type="button"
                            onClick={() => line.billItemId ? removeSavedBillingItem(line) : setAppendedLines((prev) => prev.filter((item) => item.key !== line.key))}
                            className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-alert-red/10 hover:text-alert-red"
                            title="Remove billing-added item"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              <BillingPrintDocument
                visit={selectedVisit}
                billId={selectedVisit.bill?.id}
                contact={contact}
                date={formatDate()}
                billedBy={user?.name || user?.email || "-"}
                lines={allBillLines}
                totalAmount={totalAmount}
              />
            </>
          )}
        </main>
      </div>
    </SidebarLayout>
  );
};

export default AddOrEditBill;
